import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@/lib/use-server-fn";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { adminSettings, adminSaveSettings } from "@/lib/admin.functions";
import { adminSettingsQuery } from "@/lib/admin-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/admin/ImageUpload";

type FieldDef = { key: string; label: string; long?: boolean; image?: boolean; hint?: string };

const GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "Identidade visual",
    fields: [
      {
        key: "logo_url",
        label: "Logo da campanha (topo e rodapé do site)",
        image: true,
        hint: "PNG com fundo transparente funciona melhor.",
      },
      {
        key: "logo_light_url",
        label: "Logo versão branca (para fundos escuros, ex.: rodapé)",
        image: true,
        hint: "PNG com fundo transparente e traços em branco.",
      },
      {
        key: "hero_bg_url",
        label: "Imagem de fundo do destaque da home",
        image: true,
        hint: "Foto de fundo do bloco roxo da home — o roxo é aplicado por cima com transparência.",
      },
      {
        key: "page_hero_bg_url",
        label: "Imagem de fundo do topo das páginas internas",
        image: true,
        hint: "Usada no bloco roxo no topo das demais páginas.",
      },
      {
        key: "hero_photo_url",
        label: "Foto do candidato (destaque da home)",
        image: true,
        hint: "PNG recortado do candidato — substitui o card de números no topo.",
      },
    ],
  },
  {
    title: "Candidato",
    fields: [
      { key: "candidate_name", label: "Nome do candidato" },
      { key: "candidate_number", label: "Número" },
      { key: "office", label: "Cargo" },
      { key: "slogan", label: "Slogan" },
      { key: "city", label: "Cidade" },
      { key: "state", label: "Estado" },
    ],
  },
  {
    title: "Hero da home",
    fields: [
      { key: "hero_tagline", label: "Tagline" },
      { key: "hero_title", label: "Título" },
      { key: "hero_subtitle", label: "Subtítulo", long: true },
    ],
  },
  {
    title: "Contato",
    fields: [
      { key: "email", label: "E-mail" },
      { key: "phone", label: "Telefone" },
      { key: "whatsapp", label: "WhatsApp" },
      { key: "address", label: "Endereço", long: true },
    ],
  },
  {
    title: "Redes sociais",
    fields: [
      { key: "instagram", label: "Instagram" },
      { key: "facebook", label: "Facebook" },
      { key: "youtube", label: "YouTube" },
      { key: "tiktok", label: "TikTok" },
    ],
  },
  {
    title: "Rodapé e legal",
    fields: [
      { key: "footer_text", label: "Texto do rodapé", long: true },
      { key: "cnpj", label: "CNPJ de campanha" },
    ],
  },
];

function toText(v: any): string {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

function Page() {
  const callSettings = useServerFn(adminSettings);
  const callSave = useServerFn(adminSaveSettings);
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    ...adminSettingsQuery(),
    queryFn: () => callSettings(),
  });

  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const row of data as { key: string; value: any }[]) {
      next[row.key] = toText(row.value);
    }
    setValues(next);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      callSave({
        data: {
          entries: GROUPS.flatMap((g) => g.fields).map((f) => ({
            key: f.key,
            value: values[f.key] ?? "",
          })),
        },
      }),
    onSuccess: () => {
      toast.success("Configurações salvas");
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      qc.invalidateQueries({ queryKey: ["site_settings"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao salvar"),
  });

  if (isLoading) {
    return <Loader2 className="size-6 animate-spin text-primary" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-foreground">Configurações</h1>
          <p className="text-muted-foreground">Dados gerais exibidos no site.</p>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Save className="mr-2 size-4" />
          )}
          Salvar
        </Button>
      </div>

      {GROUPS.map((group) => (
        <section key={group.title} className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-4 font-display text-lg text-foreground">{group.title}</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {group.fields.map((f) => (
              <div key={f.key} className={f.long || f.image ? "md:col-span-2" : ""}>
                <Label htmlFor={f.key}>{f.label}</Label>
                {f.image ? (
                  <div className="mt-2 space-y-2">
                    <ImageUpload
                      value={values[f.key] ?? ""}
                      onChange={(url) => setValues((v) => ({ ...v, [f.key]: url ?? "" }))}
                      folder="identidade"
                    />
                    {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
                  </div>
                ) : f.long ? (
                  <Textarea
                    id={f.key}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    rows={3}
                  />
                ) : (
                  <Input
                    id={f.key}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export const Route = createFileRoute("/admin/configuracoes")({ component: Page });
